import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./hooks/useAuth";
import { login, logout, emailLogin, emailRegister } from "./lib/auth";
import { listRealms, createRealm, migrateDataToRealm } from "./lib/api";
import { Sidebar } from "./components/Sidebar";
import { ChatView } from "./components/ChatView";
import { ModelCompare } from "./components/ModelCompare";
import { AdminPanel } from "./components/AdminPanel";
import { SettingsPanel } from "./components/SettingsPanel";
import { IntegrationsView } from "./components/IntegrationsView";
import { SkillsView } from "./components/SkillsView";
import { TasksView } from "./components/TasksView";
import { WebTerminal } from "./components/WebTerminal";
import { FileEditor } from "./components/FileEditor";
import { MobileNav } from "./components/MobileNav";

export type ViewType = "chat" | "tasks" | "integrations" | "skills" | "admin" | "settings" | "models";

const PATH_TO_VIEW: Record<string, ViewType> = {
  "/home": "chat",
  "/tasks": "tasks",
  "/admin": "admin",
  "/settings": "settings",
  "/models": "models",
  "/integrations": "integrations",
  "/skills": "skills",
};

const VIEW_TO_PATH: Record<ViewType, string> = {
  chat: "/home",
  tasks: "/tasks",
  admin: "/admin",
  settings: "/settings",
  models: "/models",
  integrations: "/integrations",
  skills: "/skills",
};

function viewFromPath(): ViewType {
  return PATH_TO_VIEW[window.location.pathname] || "chat";
}

export default function App() {
  const { user, loading } = useAuth();
  const [view, setViewState] = useState<ViewType>(viewFromPath);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [realms, setRealms] = useState<any[]>([]);
  const [activeRealmId, setActiveRealmId] = useState<string | null>(localStorage.getItem("active_realm_id"));
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [newRealmName, setNewRealmName] = useState("");
  const [newRealmType, setNewRealmType] = useState<"personal" | "business">("personal");

  // Sync view ↔ URL
  const setView = useCallback((v: ViewType) => {
    setViewState(v);
    const target = VIEW_TO_PATH[v];
    if (window.location.pathname !== target) {
      window.history.pushState(null, "", target);
    }
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const onPop = () => setViewState(viewFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Persist sidebar state
  useEffect(() => {
    const saved = localStorage.getItem("sidebar_collapsed");
    if (saved === "true") setSidebarCollapsed(true);
  }, []);

  // Load realms
  useEffect(() => {
    if (!user) return;
    listRealms().then(({ realms: r }) => {
      setRealms(r || []);
      if (r.length === 0) {
        setShowOnboarding(true);
      } else if (!activeRealmId) {
        const def = r.find((x: any) => x.is_default) || r[0];
        if (def) handleRealmChange(def.id);
      }
    }).catch(() => {});
  }, [user]);

  const handleRealmChange = useCallback((id: string | null) => {
    setActiveRealmId(id);
    if (id) localStorage.setItem("active_realm_id", id);
    else localStorage.removeItem("active_realm_id");
    setCurrentSessionId(null); // reset chat when switching realms
  }, []);

  const handleCreateRealm = async () => {
    if (!newRealmName.trim()) return;
    const result = await createRealm({ name: newRealmName.trim(), type: newRealmType, color: newRealmType === "personal" ? "#ec4899" : "#3b82f6" });
    const updated = await listRealms();
    setRealms(updated.realms || []);
    // If first realm, migrate existing data into it
    if (result.is_default) {
      await migrateDataToRealm(result.id).catch(() => {});
    }
    handleRealmChange(result.id);
    setNewRealmName("");
    setOnboardingStep(prev => prev + 1);
  };

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem("sidebar_collapsed", String(newState));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-background gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
          <span className="text-xl font-extrabold text-white">AI</span>
        </div>
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    if (window.location.pathname !== "/login" && window.location.pathname !== "/signup") {
      window.history.replaceState(null, "", "/login");
    }
    return <LoginScreen />;
  }

  if (window.location.pathname === "/login" || window.location.pathname === "/signup" || window.location.pathname === "/") {
    window.history.replaceState(null, "", VIEW_TO_PATH[view]);
  }

  // First-run onboarding
  if (showOnboarding) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-background px-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6">
          <span className="text-3xl font-extrabold text-white">AI</span>
        </div>
        {onboardingStep === 0 ? (
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Welcome to AI App</h1>
            <p className="text-muted-foreground mb-8">Let's set up your first realm. Realms separate your personal life from business — each with its own connections, repos, and workspaces.</p>
            <div className="bg-card border border-border rounded-xl p-6 text-left space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1.5">Realm Name</label>
                <input
                  value={newRealmName}
                  onChange={(e) => setNewRealmName(e.target.value)}
                  placeholder="e.g. Personal, AITWIRE, Consulting"
                  autoFocus
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1.5">Type</label>
                <div className="flex gap-2">
                  {(["personal", "business"] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setNewRealmType(t)}
                      className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        newRealmType === t
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card text-muted-foreground border-border hover:text-foreground"
                      }`}
                    >
                      {t === "personal" ? "🏠 Personal" : "🏢 Business"}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleCreateRealm}
                disabled={!newRealmName.trim()}
                className="w-full py-2.5 bg-success text-success-foreground font-medium rounded-lg hover:bg-success/90 disabled:opacity-50"
              >
                Create Realm
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-foreground mb-2">✓ Realm Created!</h2>
            <p className="text-muted-foreground mb-6">You can add more realms anytime in Settings. Each realm gets its own Google connection, GitHub repos, and workspaces.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setOnboardingStep(0); setNewRealmName(""); }}
                className="px-6 py-2.5 bg-card border border-border text-muted-foreground font-medium rounded-lg hover:text-foreground"
              >
                Add Another Realm
              </button>
              <button
                onClick={() => setShowOnboarding(false)}
                className="px-6 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90"
              >
                Start Using AI App
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col md:flex-row bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <Sidebar
          user={user}
          view={view}
          setView={setView}
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          onLogout={logout}
          currentSessionId={currentSessionId}
          onSelectSession={setCurrentSessionId}
          activeRealmId={activeRealmId}
          onRealmChange={handleRealmChange}
          realms={realms}
        />
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden flex flex-col pb-16 md:pb-0">
        {view === "chat" && (
          <WorkspaceView
            sessionId={currentSessionId}
            onSessionChange={setCurrentSessionId}
          />
        )}
        {view === "tasks" && <TasksView />}
        {view === "integrations" && <IntegrationsView onAskAI={() => {
          setCurrentSessionId(null);
          setView("chat");
        }} />}
        {view === "skills" && <SkillsView onAskAI={(context) => {
          setCurrentSessionId(null);
          setView("chat");
          // Small delay so chat view mounts before context is needed
          setTimeout(() => (window as any).__pendingChatContext = context, 50);
        }} />}
        {view === "admin" && <AdminPanel user={user} />}
        {view === "settings" && <SettingsPanel />}
        {view === "models" && <ModelCompare />}
      </main>

      {/* Mobile Bottom Nav */}
      <MobileNav view={view} setView={setView} onLogout={logout} />
    </div>
  );
}

function WorkspaceView({ sessionId, onSessionChange }: { sessionId: string | null; onSessionChange: (id: string | null) => void }) {
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [terminalHeight, setTerminalHeight] = useState(300);
  const [editorFile, setEditorFile] = useState<{ content: string; path: string; repo: string } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Allow chat to open files in the editor
  (window as any).__openFileInEditor = (content: string, path: string, repo: string) => {
    setEditorFile({ content, path, repo });
  };

  // Drag to resize terminal
  const handleDragStart = () => setDragging(true);
  const handleDrag = (e: React.MouseEvent) => {
    if (!dragging) return;
    const container = e.currentTarget.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const newHeight = rect.bottom - e.clientY;
    setTerminalHeight(Math.max(100, Math.min(newHeight, rect.height - 200)));
  };
  const handleDragEnd = () => setDragging(false);

  return (
    <div
      className="flex flex-col h-full"
      onMouseMove={dragging ? handleDrag : undefined}
      onMouseUp={dragging ? handleDragEnd : undefined}
      onMouseLeave={dragging ? handleDragEnd : undefined}
    >
      {/* Main area: Chat (+ optional editor overlay) */}
      <div className="flex-1 overflow-hidden flex">
        {/* Chat — always visible */}
        <div className={`${editorFile ? "w-1/2 border-r border-border" : "w-full"} h-full`}>
          <ChatView sessionId={sessionId} onSessionChange={onSessionChange} />
        </div>
        {/* Editor — side by side when open */}
        {editorFile && (
          <div className="w-1/2 h-full">
            <FileEditor
              content={editorFile.content}
              path={editorFile.path}
              repo={editorFile.repo}
              onClose={() => setEditorFile(null)}
              onSave={() => {}}
              className="h-full"
            />
          </div>
        )}
      </div>

      {/* Terminal — bottom panel, collapsible */}
      {terminalOpen && (
        <>
          {/* Drag handle */}
          <div
            onMouseDown={handleDragStart}
            className="h-1.5 bg-border hover:bg-primary/50 cursor-row-resize flex-shrink-0 flex items-center justify-center"
          >
            <div className="w-8 h-0.5 bg-muted-foreground/30 rounded" />
          </div>
          <div style={{ height: terminalHeight }} className="flex-shrink-0">
            <WebTerminal className="h-full" />
          </div>
        </>
      )}

      {/* Bottom bar: terminal toggle */}
      {!terminalOpen && (
        <div className="flex items-center px-3 py-1 border-t border-border bg-card">
          <button
            onClick={() => setTerminalOpen(true)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>▲</span> Terminal
          </button>
        </div>
      )}
      {terminalOpen && (
        <div className="flex items-center px-3 py-0.5 border-t border-border bg-card">
          <button
            onClick={() => setTerminalOpen(false)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>▼</span> Hide Terminal
          </button>
        </div>
      )}
    </div>
  );
}

function LoginScreen() {
  const [mode, setModeState] = useState<"login" | "signup">(
    window.location.pathname === "/signup" ? "signup" : "login"
  );

  const setMode = (m: "login" | "signup") => {
    setModeState(m);
    window.history.replaceState(null, "", m === "signup" ? "/signup" : "/login");
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "signup") {
      if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
      if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    }

    setIsLoading(true);
    try {
      if (mode === "login") {
        await emailLogin(email, password);
      } else {
        await emailRegister(email, password, name || undefined);
      }
      window.location.reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full bg-background px-6">
      {/* Logo */}
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6 shadow-lg shadow-primary/30">
        <span className="text-4xl font-extrabold text-white">AI</span>
      </div>

      {/* Brand */}
      <h1 className="text-2xl font-bold text-foreground tracking-tight">AI App</h1>
      <p className="text-muted-foreground text-sm mt-2 mb-6 text-center max-w-xs leading-relaxed">
        {mode === "login" ? "Sign in to your account" : "Create your account"}
      </p>

      <div className="w-full max-w-sm">
        {/* OAuth buttons */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => login("google")}
            className="flex-1 bg-card hover:bg-card/80 text-secondary-foreground font-medium py-2.5 px-4 rounded-xl border border-border transition-colors text-sm"
          >
            Google
          </button>
          <button
            onClick={() => login("github")}
            className="flex-1 bg-card hover:bg-card/80 text-secondary-foreground font-medium py-2.5 px-4 rounded-xl border border-border transition-colors text-sm"
          >
            GitHub
          </button>
        </div>

        {/* Divider */}
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">or</span></div>
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-400">
              {error}
            </div>
          )}

          {mode === "signup" && (
            <div>
              <label htmlFor="name" className="block text-xs font-medium text-muted-foreground mb-1">Name <span className="opacity-50">(optional)</span></label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How should we address you?"
                disabled={isLoading}
                autoComplete="name"
                className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50"
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={isLoading}
              autoComplete="email"
              className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-medium text-muted-foreground mb-1">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "At least 8 characters" : "Enter your password"}
                required
                disabled={isLoading}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                className="w-full bg-card border border-border rounded-xl px-4 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                )}
              </button>
            </div>
          </div>

          {mode === "signup" && (
            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-medium text-muted-foreground mb-1">Confirm password</label>
              <input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                disabled={isLoading}
                autoComplete="new-password"
                className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 mt-1"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                {mode === "login" ? "Signing in..." : "Creating account..."}
              </span>
            ) : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        {/* Toggle mode */}
        <p className="text-center text-sm text-muted-foreground mt-4">
          {mode === "login" ? (
            <>Don&apos;t have an account?{" "}<button onClick={() => { setMode("signup"); setError(""); }} className="font-medium text-primary hover:text-primary/80">Sign up</button></>
          ) : (
            <>Already have an account?{" "}<button onClick={() => { setMode("login"); setError(""); }} className="font-medium text-primary hover:text-primary/80">Sign in</button></>
          )}
        </p>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 flex gap-4 text-xs text-muted-foreground">
        <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
        <a href="#" className="hover:text-foreground transition-colors">Terms</a>
        <a href="#" className="hover:text-foreground transition-colors">Support</a>
      </div>
    </div>
  );
}
